/* global YoastSEO, smartContentSyncYoastData */

/**
 * Smart Content Sync → Yoast SEO analysis bridge.
 *
 * Yoast analyzes the "content" of the post/page in the editor. Smart Content Sync can inject
 * additional content (shortcodes, auto-insert rules, etc.) at render time, so Yoast would miss it.
 *
 * This bridge fetches the server-expanded HTML for the currently edited post and appends it to
 * Yoast's analyzed content via a Yoast "content" modification.
 *
 * The REST endpoint and nonce are localized by YoastIntegration.php into `smartContentSyncYoastData`.
 */
class SmartContentSyncYoastSeoAnalysisBridge {
  constructor() {
    this.cachedExtraContent = '';
    this.isFetching = false;
    // Debug logging is opt-in (enabled only when localized data explicitly enables it).
    this.debugEnabled =
      !!(
        typeof window.smartContentSyncYoastData !== 'undefined' && window.smartContentSyncYoastData
      ) && window.smartContentSyncYoastData.debug === true;

    // Classic Editor: track TinyMCE binding so we can reattach after re-init.
    this.tinymceEditor = null;
    this.tinymceListenerBound = false;
    this.classicListenersRegistered = false;

    this.lastLoggedHasExtra = null;
    this.lastLoggedExtraLen = null;

    this.lastObservedContentHash = '';
    this.debouncedRefetch = this.debounce(() => {
      this.debug('debouncedRefetch() running');
      // Invalidate cache and refetch, then request Yoast refresh once we have data.
      this.cachedExtraContent = '';
      this.fetchExtraContent();
    }, 600);

    this.onYoastReady = this.onYoastReady.bind(this);
    this.modifyContent = this.modifyContent.bind(this);
  }

  debounce(fn, waitMs) {
    let t = null;
    return (...args) => {
      if (t) {
        clearTimeout(t);
      }
      t = setTimeout(() => fn(...args), waitMs);
    };
  }

  debug(...args) {
    if (!this.debugEnabled) {
      return;
    }
    // eslint-disable-next-line no-console
    console.debug('[SmartContentSync][Yoast]', ...args);
  }

  init() {
    this.debug('init()');
    // Yoast triggers a ready event; use it if YoastSEO isn't available yet.
    if (typeof window.YoastSEO === 'undefined' || !window.YoastSEO.app) {
      if (window.jQuery) {
        window.jQuery(window).on('YoastSEO:ready', this.onYoastReady);
      } else {
        this.pollForYoast();
      }
      return;
    }

    this.onYoastReady();
  }

  pollForYoast() {
    this.debug('pollForYoast() start');
    const maxTries = 50;
    let tries = 0;

    const timer = setInterval(() => {
      tries += 1;

      if (typeof window.YoastSEO !== 'undefined' && window.YoastSEO.app) {
        this.debug('Yoast detected during poll; initializing');
        clearInterval(timer);
        this.onYoastReady();
        return;
      }

      if (tries >= maxTries) {
        this.debug('Yoast not detected after polling; giving up');
        clearInterval(timer);
      }
    }, 200);
  }

  onYoastReady() {
    if (typeof window.YoastSEO === 'undefined' || !window.YoastSEO.app) {
      return;
    }
    this.debug('onYoastReady()');

    const pluginName = 'SmartContentSyncYoastSeoAnalysis';

    try {
      window.YoastSEO.app.registerPlugin(pluginName, { status: 'ready' });
    } catch (e) {
      // Ignore double registration.
    }
    this.debug('registered Yoast plugin + content modification');

    window.YoastSEO.app.registerModification('content', this.modifyContent, pluginName, 10);

    // Refetch when editor content changes (Gutenberg + Classic Editor).
    this.registerEditorChangeListeners();

    // Prime cache early so Yoast picks it up quickly.
    this.fetchExtraContent();
  }

  modifyContent(originalContent) {
    // Yoast calls modifyContent a lot; only log when the cached state changes.
    const hasExtra = !!this.cachedExtraContent;
    const extraLen = this.cachedExtraContent.length;

    if (this.lastLoggedHasExtra !== hasExtra || this.lastLoggedExtraLen !== extraLen) {
      this.lastLoggedHasExtra = hasExtra;
      this.lastLoggedExtraLen = extraLen;
      this.debug('modifyContent()', { hasExtra, extraLen });
    }
    // Yoast expects a synchronous return value. We don't block; we append cached data if available.
    if (!this.cachedExtraContent) {
      // Avoid spamming calls; fetchExtraContent() already guards concurrent fetches.
      if (!this.isFetching) {
        this.fetchExtraContent();
      }
      return originalContent;
    }

    // Expanded-only: when we have server-expanded HTML, let Yoast analyze that as the canonical content.
    // This avoids duplicate/ordered content issues (e.g. "keyphrase in introduction").
    return this.cachedExtraContent;
  }

  registerClassicEditorListeners() {
    if (this.classicListenersRegistered) {
      return;
    }
    this.classicListenersRegistered = true;

    // Textarea fallback always (covers Text mode and cases where TinyMCE is not active).
    const textarea = document.getElementById('content');
    if (textarea) {
      textarea.addEventListener('input', () => {
        this.debug('Classic Editor textarea changed; scheduling refetch');
        this.debouncedRefetch();
      });
      textarea.addEventListener('change', () => {
        this.debug('Classic Editor textarea change; scheduling refetch');
        this.debouncedRefetch();
      });
    }

    // Attach to an existing TinyMCE instance if present.
    this.attachTinyMceListeners();

    // Re-attach whenever WordPress re-initializes TinyMCE (common when switching Visual/Text or metabox refreshes).
    try {
      if (window.jQuery) {
        window.jQuery(document).on('tinymce-editor-init', (evt, editor) => {
          if (editor && editor.id === 'content') {
            this.debug('tinymce-editor-init detected; reattaching listeners');
            this.attachTinyMceListeners(editor);
          }
        });
      }
    } catch (e) {
      // ignore
    }

    // Also listen to TinyMCE manager events (covers cases where the jQuery event isn't fired).
    try {
      if (window.tinymce && window.tinymce.on) {
        window.tinymce.on('AddEditor', (e) => {
          const editor = e && e.editor ? e.editor : null;
          if (editor && editor.id === 'content') {
            this.debug('tinymce AddEditor detected; reattaching listeners');
            this.attachTinyMceListeners(editor);
          }
        });

        window.tinymce.on('RemoveEditor', (e) => {
          const editor = e && e.editor ? e.editor : null;
          if (editor && editor.id === 'content') {
            this.debug('tinymce RemoveEditor detected; clearing binding');
            this.tinymceEditor = null;
            this.tinymceListenerBound = false;
          }
        });
      }
    } catch (e) {
      // ignore
    }
  }

  attachTinyMceListeners(editorOverride = null) {
    try {
      if (!(window.tinymce && typeof window.tinymce.get === 'function')) {
        return;
      }

      const editor = editorOverride || window.tinymce.get('content');
      if (!editor || typeof editor.on !== 'function') {
        return;
      }

      // If we already bound to this exact editor instance, do nothing.
      if (this.tinymceEditor === editor && this.tinymceListenerBound) {
        return;
      }

      // If we were bound to a previous editor instance, best-effort unbind.
      if (
        this.tinymceEditor &&
        this.tinymceEditor !== editor &&
        typeof this.tinymceEditor.off === 'function'
      ) {
        try {
          this.tinymceEditor.off('change keyup SetContent Undo Redo', this._onTinyMceContentChange);
        } catch (e) {
          // ignore
        }
      }

      // Bind a stable handler reference so we can unbind later.
      this._onTinyMceContentChange = () => {
        this.debug('Classic Editor TinyMCE changed; scheduling refetch');
        this.debouncedRefetch();
      };

      editor.on('change keyup SetContent Undo Redo', this._onTinyMceContentChange);

      this.tinymceEditor = editor;
      this.tinymceListenerBound = true;

      this.debug('TinyMCE listeners attached', { editorId: editor.id });
    } catch (e) {
      // ignore
    }
  }

  registerEditorChangeListeners() {
    this.debug('registerEditorChangeListeners()');
    // Gutenberg: use wp.data.subscribe to detect edited post content changes.
    try {
      if (window.wp && window.wp.data && typeof window.wp.data.subscribe === 'function') {
        const select = window.wp.data.select;
        const subscribe = window.wp.data.subscribe;

        let lastContent = null;
        let lastPostId = null;

        subscribe(() => {
          try {
            const editorStore = select('core/editor');
            if (!editorStore || typeof editorStore.getEditedPostContent !== 'function') {
              return;
            }

            const postId =
              typeof editorStore.getCurrentPostId === 'function'
                ? Number(editorStore.getCurrentPostId())
                : null;

            const content = editorStore.getEditedPostContent();

            // First subscribe tick: capture initial state without scheduling a refetch
            // (we already prime the cache in onYoastReady()).
            if (lastPostId === null && postId) {
              lastPostId = postId;
              lastContent = content;
              return;
            }

            // If we switched posts in the editor context, invalidate and refetch.
            if (postId && postId !== lastPostId) {
              lastPostId = postId;
              lastContent = content;
              this.debug('Gutenberg postId changed; scheduling refetch', { postId });
              this.debouncedRefetch();
              return;
            }

            // Only refetch when content actually changed.
            if (content !== lastContent) {
              lastContent = content;
              this.debug('Gutenberg content changed; scheduling refetch');
              this.debouncedRefetch();
            }
          } catch (e) {
            // ignore
          }
        });

        // return;
      }
    } catch (e) {
      // ignore
    }

    // Classic Editor: attach resilient listeners (re-attaches after TinyMCE re-init).
    this.registerClassicEditorListeners();
  }

  getCurrentEditorContent() {
    // Gutenberg
    try {
      if (window.wp && window.wp.data && typeof window.wp.data.select === 'function') {
        const editorStore = window.wp.data.select('core/editor');
        if (editorStore && typeof editorStore.getEditedPostContent === 'function') {
          const content = editorStore.getEditedPostContent();
          if (typeof content === 'string' && content.trim() !== '') {
            return content;
          }
        }
      }
    } catch (e) {
      // ignore
    }

    // Classic Editor (TinyMCE)
    try {
      if (window.tinymce && typeof window.tinymce.get === 'function') {
        const editor = window.tinymce.get('content');
        if (editor && typeof editor.getContent === 'function') {
          const content = editor.getContent({ format: 'raw' });
          if (typeof content === 'string' && content.trim() !== '') {
            return content;
          }
        }
      }
    } catch (e) {
      // ignore
    }

    // Classic Editor (textarea fallback)
    const textarea = document.getElementById('content');
    if (textarea && typeof textarea.value === 'string' && textarea.value.trim() !== '') {
      return textarea.value;
    }

    return '';
  }

  getEditorContentForRequest() {
    const editorContent = this.getCurrentEditorContent();
    if (typeof editorContent !== 'string') {
      return { editorContent: '', hasEditorContent: false, isTooLarge: false };
    }

    const trimmed = editorContent.trim();
    if (trimmed === '') {
      return { editorContent: '', hasEditorContent: false, isTooLarge: false };
    }

    // Safety: avoid posting very large payloads from the editor.
    // If it’s too large, we’ll fall back to GET (saved DB content).
    const maxLen = 200000; // ~200 KB of text; conservative and avoids proxy/server limits.
    const isTooLarge = trimmed.length > maxLen;

    return {
      editorContent: editorContent,
      hasEditorContent: !isTooLarge,
      isTooLarge,
    };
  }

  async fetchExtraContent() {
    if (this.isFetching) {
      return;
    }
    this.debug('fetchExtraContent() start');

    const postId =
      smartContentSyncYoastData && smartContentSyncYoastData.postId
        ? Number(smartContentSyncYoastData.postId)
        : 0;

    const endpointUrl =
      smartContentSyncYoastData && smartContentSyncYoastData.endpointUrl
        ? String(smartContentSyncYoastData.endpointUrl)
        : '';

    const nonce =
      smartContentSyncYoastData && smartContentSyncYoastData.nonce
        ? String(smartContentSyncYoastData.nonce)
        : '';

    if (!postId || !endpointUrl || !nonce) {
      return;
    }
    const { editorContent, hasEditorContent, isTooLarge } = this.getEditorContentForRequest();
    this.debug('fetchExtraContent() editorContent', {
      hasEditorContent,
      isTooLarge,
      len: typeof editorContent === 'string' ? editorContent.length : 0,
    });

    this.isFetching = true;

    try {
      let response = null;

      if (hasEditorContent) {
        response = await fetch(endpointUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'X-WP-Nonce': nonce,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            postId,
            editorContent,
          }),
        });
      } else {
        const url = new URL(endpointUrl, window.location.origin);
        url.searchParams.set('postId', String(postId));

        response = await fetch(url.toString(), {
          method: 'GET',
          credentials: 'same-origin',
          headers: {
            'X-WP-Nonce': nonce,
            Accept: 'application/json',
          },
        });
      }

      if (!response.ok) {
        this.debug('fetchExtraContent() non-OK response', { status: response.status });
        const previous = this.cachedExtraContent;
        this.cachedExtraContent = '';

        // If we previously had content, ask Yoast to re-run analysis without the extra HTML.
        if (previous) {
          if (
            window.YoastSEO &&
            window.YoastSEO.app &&
            typeof window.YoastSEO.app.refresh === 'function'
          ) {
            window.YoastSEO.app.refresh();
          }
        }

        return;
      }

      const json = await response.json();
      const html = json && typeof json.content === 'string' ? json.content : '';

      const next = html ? String(html) : '';
      const changed = next !== this.cachedExtraContent;

      this.cachedExtraContent = next;
      this.debug('fetchExtraContent() success', {
        extraLen: this.cachedExtraContent.length,
        changed,
      });

      // Ask Yoast to re-run analysis only when the analysis input changed.
      if (
        changed &&
        window.YoastSEO &&
        window.YoastSEO.app &&
        typeof window.YoastSEO.app.refresh === 'function'
      ) {
        window.YoastSEO.app.refresh();
      }
    } catch (e) {
      this.debug('fetchExtraContent() error', e);
      this.cachedExtraContent = '';
    } finally {
      this.isFetching = false;
    }
  }
}

try {
  new SmartContentSyncYoastSeoAnalysisBridge().init();
} catch (e) {
  // no-op
}
